import { useState, useCallback, useRef } from "react";

// A plain ref + effect-on-mount only attaches the observer once, when the
// hook first runs. Sections that render conditionally on data that arrives
// after mount (e.g. FAQs/features/related items populated by an async API
// call) don't exist in the DOM yet at that point, so the observer never
// attaches and the section stays permanently opacity: 0 once it does mount.
// A callback ref re-attaches every time React gives it a new DOM node,
// including that later mount, so it's always watching once the element
// actually exists.
const useScrollReveal = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef(null);
  const { threshold = 0.12, rootMargin = "0px 0px -40px 0px" } = options;

  const ref = useCallback(
    (el) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(el);
          }
        },
        { threshold, rootMargin }
      );
      observer.observe(el);
      observerRef.current = observer;
    },
    [threshold, rootMargin]
  );

  return [ref, isVisible];
};

export default useScrollReveal;
